import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@zond/ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { getAppName, getLogo } from '../../../utils/get-logo';
import { useRequestPasswordReset } from './auth.queries';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const { mutate: requestPasswordReset, isPending, isSuccess } = useRequestPasswordReset();

  const forgotPasswordSchema = z.object({
    email: z.string().email(t('validation.invalidEmailShort')),
  });

  const form = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: z.infer<typeof forgotPasswordSchema>) => {
    requestPasswordReset(data.email);
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex items-center gap-2 self-center font-medium">
            <img src={getLogo()} alt={t('logoAlt')} className="h-6 w-6" />
            {getAppName()}
          </div>
          <Card className="w-full">
            <CardHeader className="text-center">
              <CardTitle className="text-xl">{t('forgotPassword.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center">{t('forgotPassword.success')}</p>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <div className="text-sm text-center">
                <Link to="/login" className="underline underline-offset-4 text-primary">
                  {t('forgotPassword.backToSignIn')}
                </Link>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex items-center gap-2 self-center font-medium">
          <img src={getLogo()} alt={t('logoAlt')} className="h-6 w-6" />
          {getAppName()}
        </div>
        <Card className="w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t('forgotPassword.title')}</CardTitle>
            <CardDescription>{t('forgotPassword.description')}</CardDescription>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent>
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common:labels.email')}</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} placeholder="email@example.com" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button type="submit" className="w-full" disabled={isPending}>
                  {isPending ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                </Button>
                <Button variant="link" className="px-0" asChild>
                  <Link to="/login">{t('forgotPassword.backToSignIn')}</Link>
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}
